import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Pagination from "../../../../../components/pagination/Pagination";
import { PulseLoader } from "react-spinners";
import AdminActionsPopupNew from "../../admin-actions-popup/AdminActionsPopupNew";
import RolesTableHeaderNew from "../roles-table-header-new/RolesTableHeaderNew";
import RoleRowNew from "../role-row-new/RoleRowNew";
import AdminDownloadConfirmPopupNew from "../../admin-download-confirm-popup/admin-download-confirm-popup-new";
import PlaceholderEmptyScreen from "../../../../../components/PlaceholderEmptyScreen";
import placeholderImage from "../../../../../assets/img/no-project-template.svg";

import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectUser } from "../../../../../redux/user/user-selector";
import {
  selectSearchRolesQuery,
  selectRoles,
  selectNumRoles,
  selectIsFetchingRoles,
  selectSelectedRoles,
} from "../../../../../redux/admin/roles/roles-selector";
import {
  fetchRoles,
  setSelectedRoles,
} from "../../../../../redux/admin/roles/roles-actions";
import {
  TriyoButton,
  PrimaryButton,
  SecondaryButton,
} from "../../../../../components/TriyoButton";

import { useHistory } from "react-router-dom";

const RolesTableContainerNew = ({
  user: { privileges },
  data,
  fetchData,
  searchQuery,
  numEntries,
  loading,
  selectedRows,
  setSelectedRows,
}) => {
  const [showAdminActionsPopup, setShowAdminActionsPopup] = useState(false);
  const [showDownloadsPanel, setShowDownloadsPanel] = useState(false);
  const [headerKey, setHeaderKey] = useState(false);
  const canCreate = privileges.Role.Add;
  const containRecords = data?.length > 0;
  const canArchive = selectedRows?.length > 0;
  const canDownload = canCreate && containRecords;

  let history = useHistory();

  useEffect(() => {
    refreshPage();
  }, []);

  const refreshPage = () => {
    setHeaderKey(!headerKey); //Will trigger component remount to reset everything in header to default state
    fetchData({
      PageSize: 10,
      PageNumber: 1,
      SortDesc: false,
      SortBy: "name",
      Filters: [],
    });
    setSelectedRows([]);
  };

  const changeData = (
    filters = searchQuery.Filters,
    sortBy = searchQuery.SortBy,
    sortDesc = searchQuery.SortDesc
  ) => {
    fetchData({
      ...searchQuery,
      Filters: filters,
      SortBy: sortBy,
      SortDesc: sortDesc,
    });
  };

  const nextPage = () => {
    const newSearchQuery = {
      ...searchQuery,
      PageNumber: searchQuery.PageNumber + 1,
    };
    fetchData(newSearchQuery);
  };

  const prevPage = () => {
    const newSearchQuery = {
      ...searchQuery,
      PageNumber: searchQuery.PageNumber - 1,
    };
    searchQuery.PageNumber >= 1 && fetchData(newSearchQuery);
  };

  const rowChange = (numRows) => {
    fetchData({ ...searchQuery, PageSize: numRows, PageNumber: 1 });
  };

  const onArchive = () => {
    if (!privileges.Department.Delete === false) {
      setShowAdminActionsPopup(true);
    }
  };

  const onClick = () => {
    history.push("/admin/roles");
  };

  const getSelectedName = (roleID) => {
    if (!selectedRows || selectedRows.length <= 0) {
      return "";
    }

    let selectedRow = data.filter(({ role }) => {
      if (role.Id === roleID) return true;
    });

    const userNameFull = selectedRow[0].role.Name;
    return userNameFull;
  };

  const renderRows = () => {
    const Rows =
      data &&
      data.map(({ role }) => {
        let role1 = { ...role, department: "department" };
        return (
          <RoleRowNew key={role.Id} data={role1} privileges={privileges} />
        );
      });
    return Rows;
  };

  return (
    <div className="admin-panel-table-container">
      {data ? (
        <React.Fragment>
          <div className="table-control-container">
            <TriyoButton>
              <PrimaryButton
                onClick={onClick}
                disabled={!canCreate}
                text="Create New Role"
              />
              <SecondaryButton
                onClick={() => {
                  setShowDownloadsPanel(true);
                }}
                disabled={!canDownload}
                text="Download Roles"
              />
              <SecondaryButton
                onClick={(e) => {
                  onArchive();
                  e.preventDefault();
                }}
                disabled={!canArchive}
                text="Archive"
              />
            </TriyoButton>
            <Pagination
              refresh={refreshPage}
              next={nextPage}
              prev={prevPage}
              rowChange={rowChange}
              numEntries={numEntries}
              query={{
                ...searchQuery,
                pageNumber: searchQuery.PageNumber,
                pageSize: searchQuery.PageSize,
              }}
            />
          </div>
          <AdminDownloadConfirmPopupNew
            show={showDownloadsPanel}
            onHide={() => setShowDownloadsPanel(false)}
            type={"roles"}
            button={"Download"}
          />
          {containRecords && (
            <div className="table-main">
              <table className="table-element">
                <RolesTableHeaderNew
                  key={headerKey} //Easy way to restart component mounting to reset to default state
                  changeData={changeData}
                  refreshPage={refreshPage}
                />
                {!loading ? (
                  <tbody>{renderRows()}</tbody>
                ) : (
                  <div className="pulse-loader">
                    <PulseLoader></PulseLoader>
                  </div>
                )}
              </table>
            </div>
          )}
          {!containRecords && (
            <PlaceholderEmptyScreen
              headerText="Create a New Role"
              textBeforeLink="Organize your team better with TRIYO. Click the "
              buttonText="Create Role"
              textAfterLink=" button to get started."
              bottomImage={placeholderImage}
              altImageText="Click to create a new role"
              onClick={() => onClick()}
            />
          )}
        </React.Fragment>
      ) : (
        <div className="pulse-loader">
          <PulseLoader></PulseLoader>
        </div>
      )}
      {showAdminActionsPopup && (
        <AdminActionsPopupNew
          type={"role"}
          name={selectedRows.length === 1 && getSelectedName(selectedRows[0])}
          selectedIDs={selectedRows}
          showAdminActionsPopup={showAdminActionsPopup}
          setShowAdminActionsPopup={setShowAdminActionsPopup}
        />
      )}
    </div>
  );
};

const mapState = createStructuredSelector({
  user: selectUser,
  searchQuery: selectSearchRolesQuery,
  data: selectRoles,
  numEntries: selectNumRoles,
  loading: selectIsFetchingRoles,
  selectedRows: selectSelectedRoles,
});

const mapDispatch = (dispatch) => ({
  fetchData: (searchQuery) => dispatch(fetchRoles(searchQuery)),
  setSelectedRows: (idList) => dispatch(setSelectedRoles(idList)),
});

RolesTableContainerNew.propTypes = {
  user: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  fetchData: PropTypes.func.isRequired,
  searchQuery: PropTypes.object.isRequired,
  numEntries: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedRows: PropTypes.array.isRequired,
  setSelectedRows: PropTypes.func.isRequired,
};
export default connect(mapState, mapDispatch)(RolesTableContainerNew);
